import { LoadingIndicator } from '../common/components/loading-indicator';
import { Unmask } from '../common/components/datadog-privacy';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { Flex, HStack, VStack, Box, Text } from '@cad-arc';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useCachedEventPanelViewQuery } from '../event-panel/event-state/use-cached-event-panel-view-query';
import { FrPage } from '../fr-page-layout/fr-page';
import { FrPageBody } from '../fr-page-layout/fr-page-body';
import { FrPageHeader } from '../fr-page-layout/fr-page-header';
import {
    useFullscreenModalControls,
    useFullscreenModalState,
} from '../modals/context/modal-context-provider';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { useTransitionUnitStatusMutation } from '../unit-status/use-transition-unit-status-mutation';
import { FR_CREATE_EVENT_SUB_PATH, FR_EVENT_ROOT_PATH } from '../router/constants';
import { useUnitLocation } from '../fr-unit-location/use-unit-location';
import { useFrState } from '../fr-state/use-fr-state';
import { getHaversineDistanceBetweenTwoEntities, isValidLatLng } from '../location/helpers';
import { colors } from '../colors/colors';

function SectionHeader(props: { title: string }) {
    return (
        <Text variant="headingSm" color="tertiary">
            {props.title}
        </Text>
    );
}

function TileSection(props: { title: string; paddingBottom?: string; children: React.ReactNode }) {
    const { paddingBottom = '6' } = props;
    return (
        <VStack alignItems="flex-start" pb={paddingBottom}>
            <SectionHeader title={props.title} />
            <HStack wrap="wrap" gap="24px">
                {props.children}
            </HStack>
        </VStack>
    );
}

type TileProps = {
    children?: React.ReactNode;
    text?: string;
    onClick: () => void;
    autoFocus?: boolean;
    height?: string;
    justifyContent?: string;
};

function Tile(props: TileProps) {
    const ref = useRef<null | HTMLDivElement>(null);
    const { height = '80px', justifyContent = 'center' } = props;

    useEffect(() => {
        if (props.autoFocus) {
            ref.current?.focus();
        }
    }, [props.autoFocus]);

    return (
        <Box
            ref={ref}
            cursor="pointer"
            display="flex"
            textAlign="center"
            borderRadius="md"
            backgroundColor="surface.foreground"
            alignItems="center"
            justifyContent={justifyContent}
            h={height}
            w="197px"
            px="3"
            style={{ marginLeft: 0 }}
            tabIndex={0}
            onClick={props.onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter') props.onClick();
            }}
            _hover={{
                backgroundColor: 'var(--arc-colors-interactive-active)',
            }}
        >
            {props.text ? (
                <Text variant="headingSm" noOfLines={2}>
                    {props.text}
                </Text>
            ) : (
                props.children
            )}
        </Box>
    );
}

function HeldEventTile(props: { heldEventId: number }) {
    const { heldEventId } = props;
    const eventQuery = useCachedEventPanelViewQuery({ eventId: heldEventId });
    const unitLocation = useUnitLocation();

    const eventMetadata = eventQuery.data?.metadata;
    const eventForm = eventQuery.data?.form;

    const eventLocationLatitude = eventMetadata?.eventLocationLatitude;
    const eventLocationLongitude = eventMetadata?.eventLocationLongitude;
    const callForServiceDescription = eventMetadata?.callForServiceDescription;
    const priorityLabel = eventForm?.priority?.label;

    const priorityLabelBgColor =
        priorityLabel === 'P1' ? colors.priorityOneColor : colors.priorityStandardBackground;

    const isValidEventLocation = isValidLatLng(eventLocationLatitude, eventLocationLongitude);
    const hasValidUnitAndEventLocations = !!(unitLocation && isValidEventLocation);

    const modalControls = useFullscreenModalControls();

    return (
        <Tile
            height="73px"
            justifyContent="flex-start"
            onClick={() =>
                modalControls.open({
                    type: 'SELECT_HELD_EVENT_MODAL',
                    props: {
                        initialHeldEventId: heldEventId,
                    },
                })
            }
        >
            {!eventQuery.data ? (
                <HStack w="100%" h="100%" alignItems="center" justifyContent="center">
                    <LoadingIndicator />
                </HStack>
            ) : (
                <Flex columnGap="12px" alignItems="center">
                    <Box
                        display="flex"
                        w="40px"
                        h="40px"
                        flexDirection="column"
                        justifyContent="center"
                        flexShrink={0}
                        borderRadius="6px"
                        backgroundColor={priorityLabelBgColor}
                    >
                        <Text variant="headingMd">{priorityLabel}</Text>
                    </Box>
                    <Flex alignItems="center">
                        <VStack alignItems="left" spacing="0">
                            <Text variant="headingSm" align="left" noOfLines={2}>
                                {callForServiceDescription}
                            </Text>
                            {hasValidUnitAndEventLocations && (
                                <Text variant="bodyMd" align="left">
                                    {getHaversineDistanceBetweenTwoEntities({
                                        firstEntityCoordinates: unitLocation,
                                        secondEntityCoordinates: {
                                            latitude: eventLocationLatitude!,
                                            longitude: eventLocationLongitude!,
                                        },
                                    })}{' '}
                                    mi
                                </Text>
                            )}
                        </VStack>
                    </Flex>
                </Flex>
            )}
        </Tile>
    );
}

function UnitStatusSections() {
    const unitAssignment = useUnitAssignment();
    const { unitStateDataById } = useBootstrapState();
    const transitionUnitStatus = useTransitionUnitStatusMutation();
    if (!unitAssignment?.unitId || !unitAssignment.unitStateData) return <></>;
    const unitStateDatum = unitStateDataById[
        unitAssignment.unitStateData.id
    ].firstResponderTransitionToUnitStateIds.map((id) => unitStateDataById[id]);
    const statuses = unitStateDatum.filter(
        (usd) =>
            usd.unitStateGroup === 'DIRECTED_PATROL' ||
            usd.unitStateGroup === 'VIRTUAL_PATROL' ||
            usd.unitStateGroup === 'SPECIAL_ASSIGNMENT'
    );
    const admin = unitStateDatum.filter(
        (usd) => usd.unitStateGroup === 'ADMIN' || usd.unitStateGroup === 'SERVICE_ASSIGNMENT'
    );
    return (
        <>
            {statuses.length > 0 ? (
                <Unmask>
                    <TileSection title="Statuses">
                        {statuses.map((status) => (
                            <Tile
                                key={status.id}
                                text={status.value}
                                onClick={() =>
                                    transitionUnitStatus.mutateAsync({
                                        unitStateData: status,
                                    })
                                }
                            />
                        ))}
                    </TileSection>
                </Unmask>
            ) : undefined}
            {admin.length > 0 ? (
                <Unmask>
                    <TileSection title="Admin" paddingBottom="0">
                        {admin.map((status) => (
                            <Tile
                                key={status.id}
                                text={status.value}
                                onClick={() => {
                                    transitionUnitStatus.mutateAsync({
                                        unitStateData: status,
                                    });
                                }}
                            />
                        ))}
                    </TileSection>
                </Unmask>
            ) : undefined}
        </>
    );
}

export function FREventAssignmentPage() {
    const enabledFeatures = useEnabledFeatures();
    const unitAssignment = useUnitAssignment();
    const { eventPanelOptions: eventOptions } = useBootstrapState();
    const heldEvents = useMemo(() => unitAssignment?.heldEvents ?? [], [
        unitAssignment?.heldEvents,
    ]);
    const hasHeldEvents = heldEvents.length > 0;
    const navigate = useNavigate();
    const modalControls = useFullscreenModalControls();
    const modalState = useFullscreenModalState();
    const hasOtherModalAlreadyOpen = modalState !== undefined;
    const frState = useFrState();

    const eventOriginOtherOptions = useMemo(
        () => eventOptions?.eventOriginSelfInitiateOptions.filter((option) => option.isOther),
        [eventOptions?.eventOriginSelfInitiateOptions]
    );

    useEffect(() => {
        if (hasHeldEvents && !hasOtherModalAlreadyOpen && !frState.wasHeldEventsModalShown) {
            modalControls.open({
                type: 'SELECT_HELD_EVENT_MODAL',
                props: {
                    initialHeldEventId: heldEvents[0].eventId,
                },
            });
        }
    }, [
        frState.wasHeldEventsModalShown,
        hasHeldEvents,
        hasOtherModalAlreadyOpen,
        heldEvents,
        modalControls,
    ]);

    const selfInitiateCallForServiceOptions = unitAssignment?.selfInitiateCallForServiceOptions;

    return (
        <FrPage>
            <FrPageHeader title="Self-Initiate" />
            <FrPageBody>
                <Box p="4">
                    {enabledFeatures.selfInitiateEvent && (
                        <Unmask>
                            <TileSection title="Calls For Service">
                                {selfInitiateCallForServiceOptions?.map((option, index) => (
                                    <Tile
                                        autoFocus={index === 0}
                                        key={option.value}
                                        text={option.label}
                                        onClick={() => {
                                            navigate(
                                                `${FR_EVENT_ROOT_PATH}/${FR_CREATE_EVENT_SUB_PATH}?selfInitiateCallForServiceId=${option.value}`
                                            );
                                        }}
                                    />
                                ))}
                                {eventOriginOtherOptions?.map((option) => (
                                    <Tile
                                        key={option.value}
                                        text={option.label}
                                        onClick={() => {
                                            navigate(
                                                `${FR_EVENT_ROOT_PATH}/${FR_CREATE_EVENT_SUB_PATH}?eventOriginOtherOptionId=${option.value}`
                                            );
                                        }}
                                    />
                                ))}
                            </TileSection>
                        </Unmask>
                    )}
                    {hasHeldEvents && (
                        <Unmask>
                            <TileSection title={`Held Assignments (${heldEvents.length})`}>
                                {heldEvents.map((heldEvent) => (
                                    <HeldEventTile
                                        key={heldEvent.eventId}
                                        heldEventId={heldEvent.eventId}
                                    />
                                ))}
                            </TileSection>
                        </Unmask>
                    )}
                    <UnitStatusSections />
                </Box>
            </FrPageBody>
        </FrPage>
    );
}
